import React, { useEffect, useState, useCallback } from 'react';

/**
 * CryptoList
 * ----------
 * Top 10 crypto prices from the public CoinGecko markets endpoint, rendered
 * as separated dark "rows" (one card per coin) to match the wallet-style
 * design in the spec screenshot.
 *
 * - Auto-refreshes every 60s.
 * - Uses the app's existing theme tokens (--card-bg, --text-primary, etc.),
 *   so it inherits dark/light from the dashboard container.
 * - Has its own shimmer skeleton while loading.
 */
const COINGECKO_URL =
  'https://api.coingecko.com/api/v3/coins/markets' +
  '?vs_currency=usd&order=market_cap_desc&per_page=10&page=1' +
  '&sparkline=false&price_change_percentage=24h';

const REFRESH_MS = 60_000;

const CryptoList = () => {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchCoins = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await fetch(COINGECKO_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCoins(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load market data');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchCoins(true);
      if (cancelled) return;
    })();
    const id = setInterval(() => fetchCoins(false), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [fetchCoins]);

  const fmtPrice = (n) => {
    if (n === null || n === undefined || isNaN(n)) return '--';
    if (n >= 1) {
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  const fmtTime = (d) => {
    if (!d) return '';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="crypto-list-wrap">
      <style>{`
        .crypto-list-wrap {
          margin-bottom: 2rem;
          color: var(--text-primary, #fff);
        }
        .crypto-list-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0 0.25rem;
        }
        .crypto-list-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .crypto-list-title .live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--success-color, #00e676);
          box-shadow: 0 0 0 0 var(--success-color, #00e676);
          animation: cryptoPulse 1.6s infinite;
        }
        @keyframes cryptoPulse {
          0%   { box-shadow: 0 0 0 0 rgba(0,230,118,0.6); }
          70%  { box-shadow: 0 0 0 8px rgba(0,230,118,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,230,118,0); }
        }
        .crypto-list-meta {
          font-size: 0.75rem;
          color: var(--text-secondary, #b0b0b0);
        }
        .crypto-list-error {
          color: var(--danger-color, #ff3d71);
          font-size: 0.85rem;
          padding: 0.5rem 0.25rem 1rem;
        }

        /* ---------- rows ---------- */
        .crypto-rows {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .crypto-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.1rem;
          background: var(--card-bg, rgba(30,30,30,0.7));
          border: 1px solid var(--card-border, rgba(255,255,255,0.05));
          border-radius: 16px;
          transition: border-color 0.18s ease, background-color 0.18s ease;
        }
        .crypto-row:hover {
          border-color: var(--card-border-hover, rgba(0,242,254,0.3));
          background: var(--glass-effect, rgba(255,255,255,0.05));
        }
        .crypto-logo {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          flex-shrink: 0;
          object-fit: cover;
          background: rgba(255,255,255,0.04);
        }
        .crypto-id {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }
        .crypto-sym {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary, #fff);
          letter-spacing: 0.02em;
        }
        .crypto-name {
          font-size: 0.85rem;
          color: var(--text-secondary, #8a93a6);
          margin-top: 2px;
        }
        .crypto-price-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          line-height: 1.2;
          margin-right: 0.4rem;
          min-width: 0;
        }
        .crypto-price {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary, #fff);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .crypto-balance {
          font-size: 0.8rem;
          color: var(--text-secondary, #8a93a6);
          margin-top: 2px;
        }
        .crypto-change {
          font-size: 0.85rem;
          font-weight: 600;
          padding: 0.35rem 0.65rem;
          border-radius: 8px;
          color: #fff;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          flex-shrink: 0;
          min-width: 64px;
          text-align: center;
        }
        .crypto-change.up   { background: #16a34a; }
        .crypto-change.down { background: #ef4444; }

        @media (max-width: 380px) {
          .crypto-row { gap: 0.65rem; padding: 0.85rem 0.9rem; }
          .crypto-logo { width: 40px; height: 40px; }
          .crypto-sym, .crypto-price { font-size: 0.95rem; }
          .crypto-name, .crypto-balance { font-size: 0.78rem; }
          .crypto-change {
            font-size: 0.78rem;
            padding: 0.3rem 0.5rem;
            min-width: 56px;
          }
        }

        /* ---------- skeleton ---------- */
        .cl-skel {
          background: linear-gradient(
            90deg,
            var(--skeleton-base, rgba(255,255,255,0.06)) 0%,
            var(--skeleton-shine, rgba(255,255,255,0.14)) 50%,
            var(--skeleton-base, rgba(255,255,255,0.06)) 100%
          );
          background-size: 200% 100%;
          animation: cl-shimmer 1.4s ease-in-out infinite;
          border-radius: 6px;
          display: inline-block;
        }
        .cl-skel.circle { border-radius: 50%; }
        @keyframes cl-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className="crypto-list-head">
        <h6 className="crypto-list-title">
          <span className="live-dot" aria-hidden="true"></span>
          Top Cryptocurrencies — Live Prices
        </h6>
        <span className="crypto-list-meta">
          {lastUpdated
            ? `Updated ${fmtTime(lastUpdated)}`
            : loading
            ? 'Loading…'
            : ''}
        </span>
      </div>

      {error && !loading && coins.length === 0 && (
        <div className="crypto-list-error">
          Unable to load live prices ({error}). Retrying in 60s…
        </div>
      )}

      <div className="crypto-rows">
        {loading && coins.length === 0
          ? Array.from({ length: 10 }).map((_, i) => (
              <div className="crypto-row" key={`skel-${i}`}>
                <span
                  className="cl-skel circle"
                  style={{ width: 46, height: 46 }}
                />
                <div className="crypto-id">
                  <span
                    className="cl-skel"
                    style={{ width: 60, height: 14, marginBottom: 6 }}
                  />
                  <span
                    className="cl-skel"
                    style={{ width: 90, height: 11 }}
                  />
                </div>
                <div className="crypto-price-col">
                  <span
                    className="cl-skel"
                    style={{ width: 80, height: 14, marginBottom: 6 }}
                  />
                  <span
                    className="cl-skel"
                    style={{ width: 60, height: 11 }}
                  />
                </div>
                <span
                  className="cl-skel"
                  style={{
                    width: 64,
                    height: 28,
                    borderRadius: 8,
                  }}
                />
              </div>
            ))
          : coins.map((c) => {
              const change = Number(c.price_change_percentage_24h || 0);
              const isUp = change >= 0;
              return (
                <div className="crypto-row" key={c.id}>
                  <img
                    className="crypto-logo"
                    src={c.image}
                    alt={c.symbol}
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                  <div className="crypto-id">
                    <span className="crypto-sym">
                      {(c.symbol || '').toUpperCase()}
                    </span>
                    <span className="crypto-name">{c.name}</span>
                  </div>
                  <div className="crypto-price-col">
                    <span className="crypto-price">
                      ${fmtPrice(c.current_price)}
                    </span>
                    <span className="crypto-balance">No balance</span>
                  </div>
                  <span className={`crypto-change ${isUp ? 'up' : 'down'}`}>
                    {isUp ? '+' : '-'}
                    {Math.abs(change).toFixed(2)}%
                  </span>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default CryptoList;